export const FeaturesConfig = {
    "ai-site-creation": {
        name: "AI-Driven Site Creation",
        slug: "ai-site-creation",
        tagline: "Your Agent Builds. TurboSteed Ships It.",
        description: "Give Claude Code (or any coding agent) the TurboSteed MCP server and Skill. It writes the site — TurboSteed models the content, deploys it, and keeps it live.",
        heroNote: "TurboSteed doesn't generate your site's design or code — your AI agent does that. TurboSteed is the deployment and content layer underneath it.",
        icon: "icon-genius",
        heroImage: "/features/claude-ai.png",
        heroImageMaxWidth: "900px",
        capabilities: [
            "Connect Claude Code via MCP (Model Context Protocol)",
            "Use TurboSteed Skill file for guided site creation",
            "Build static HTML/CSS/JS sites from natural language",
            "Deploy in one command with automatic CDN and SSL",
            "Manage CMS schemas and entries programmatically",
            "Upload and manage assets through the agent"
        ],
        useCases: [
            { title: "Developers", description: "Automate client site creation using Claude Code and MCP" },
            { title: "Agencies", description: "Build multiple client sites quickly with AI assistance" },
            { title: "Prototyping", description: "Rapidly prototype landing pages and microsites" }
        ],
        faqs: [
            { question: "What AI agents are supported?", answer: "Currently tested and verified with Claude Code. The MCP server can work with any MCP-compatible agent." },
            { question: "Do I need coding skills?", answer: "Basic understanding of web development helps, but Claude Code handles the actual code generation." },
            { question: "Can I edit the generated site?", answer: "Yes — all generated code is standard HTML/CSS/JS that you can modify and redeploy." },
            { question: "What's the difference from the CLI?", answer: "MCP provides a bidirectional protocol for AI agents. The CLI is for direct terminal commands. Both can deploy and manage sites." }
        ]
    },
    "cms": {
        name: "CMS",
        slug: "cms",
        tagline: "Headless CMS for AI Agents",
        description: "A headless CMS built for developers and AI agents. Define content schemas, manage entries via dashboard, API, or MCP. Perfect for dynamic content workflows.",
        icon: "icon-documents",
        heroImage: "/features/cms.png",
        capabilities: [
            "Define custom content types with up to 20 fields per schema",
            "Field types: text, richtext, number, datetime, media, select",
            "RESTful API access to fetch, create, and update content",
            "MCP integration for AI agent operations",
            "CLI management with turbosteed cms commands",
            "Asset library integration for media management"
        ],
        howItWorks: [
            { title: "Define Your Schema", description: "Create content types via dashboard, CLI, or MCP with the fields you need" },
            { title: "Add Content Entries", description: "Use the visual editor, API, CLI, or AI agent to add content" },
            { title: "Fetch via API", description: "Retrieve content programmatically to render in any frontend" },
            { title: "Automate with AI", description: "Use Claude Code + MCP to manage content autonomously" }
        ],
        useCases: [
            { title: "AI-Driven Sites", description: "Let your AI agent create and manage content structures" },
            { title: "Developers", description: "Build headless sites with full API access to content" },
            { title: "Agencies", description: "Manage content for client sites with a consistent backend" }
        ],
        faqs: [
            { question: "What field types are supported?", answer: "text, richtext (HTML), number, datetime, media (URLs), and select (dropdowns)." },
            { question: "Can AI agents manage the CMS?", answer: "Yes — Claude Code with MCP can create schemas, add entries, and publish content." },
            { question: "Is there a visual editor?", answer: "Yes — the dashboard provides a visual editor for content management alongside API access." },
            { question: "How do I fetch content?", answer: "Use the REST API endpoints or the CLI to retrieve entries for your frontend." }
        ]
    },
    "analytics": {
        name: "Built-in Analytics",
        slug: "analytics",
        tagline: "Understand Your Visitors",
        description: "Track visitor counts, traffic patterns, and popular pages. Make data-driven decisions about your content.",
        icon: "icon-presentation",
        capabilities: [
            "Visitor count tracking",
            "7, 14, and 30-day views",
            "Traffic source analysis",
            "Popular pages identification"
        ],
        howItWorks: [
            { title: "Automatic Tracking", description: "Analytics are built-in — no code or setup required" },
            { title: "View Your Dashboard", description: "Access real-time stats from your dashboard or mobile app" },
            { title: "Make Decisions", description: "Use insights to improve your content and marketing" }
        ],
        useCases: [
            { title: "Track Growth", description: "See how your website traffic changes over time" },
            { title: "Measure Campaigns", description: "Understand which marketing efforts drive visitors" },
            { title: "Identify Popular Content", description: "Know which pages perform best" }
        ],
        faqs: [
            { question: "Do I need to add any tracking code?", answer: "Analytics require the TurboSteed SDK integrated with your site. The TurboSteed Skill integrates it automatically when building your site." }
        ]
    },
    "contact-forms": {
        name: "Contact Forms",
        slug: "contact-forms",
        tagline: "Capture Leads",
        description: "Built-in contact forms with webhook support. Receive submissions via dashboard, mobile app, or trigger automated workflows.",
        icon: "icon-notebook",
        capabilities: [
            "Built-in contact form widget",
            "Custom form types via API",
            "Dashboard and mobile app access",
            "Webhook triggers for automation",
            "Export to CSV"
        ],
        howItWorks: [
            { title: "Add Form Widget", description: "Include the TurboSteed contact form widget on your site" },
            { title: "Visitors Submit", description: "Visitors fill out the form on your website" },
            { title: "Get Notified", description: "View submissions in dashboard, mobile app, or via webhook" }
        ],
        useCases: [
            { title: "Lead Generation", description: "Capture potential customer inquiries" },
            { title: "Customer Service", description: "Let visitors ask questions or request support" },
            { title: "Automation", description: "Trigger workflows when forms are submitted" }
        ],
        faqs: [
            { question: "Can I customize the form fields?", answer: "Use the message field to store custom data structures for any form type." },
            { question: "Can I trigger webhooks?", answer: "Yes — configure webhook URLs to receive instant notifications on submissions." },
            { question: "Can I export my leads?", answer: "Yes, you can export all form submissions to CSV for use in other tools." }
        ]
    },
    "mobile-app": {
        name: "Mobile App",
        slug: "mobile-app",
        tagline: "View Data On The Go",
        description: "View site statistics, read contact form submissions, and upload images from your phone. Available on Android (experimental).",
        icon: "icon-mobile",
        capabilities: [
            "View visitor analytics",
            "Read and manage contact forms",
            "Upload images and media from your phone",
            "Data viewer and media uploader"
        ],
        howItWorks: [
            { title: "Download the App", description: "Get the TurboSteed app (Android, experimental)" },
            { title: "Log In", description: "Use your TurboSteed account to sign in" },
            { title: "Manage On The Go", description: "View stats, read leads, and upload images from anywhere" }
        ],
        useCases: [
            { title: "Site Owners", description: "Check your site stats during commute or between meetings" },
            { title: "Quick Response", description: "Respond to leads faster by reading submissions on your phone" },
            { title: "Content Updates", description: "Upload photos from your phone directly to your asset library" }
        ],
        faqs: [
            { question: "Is the app free?", answer: "Yes, the mobile app is free and included with all plans." },
            { question: "Can I edit my website from the app?", answer: "The app is a data viewer and media uploader. For content editing, use the web dashboard." },
            { question: "Is the app available on iOS?", answer: "Currently available only on Android (experimental). Access is provided on request." }
        ]
    },
    "custom-domains": {
        name: "Custom Domains",
        slug: "custom-domains",
        tagline: "Use Your Own Domain",
        description: "Connect your existing domain. SSL certificate included automatically for secure connections.",
        icon: "icon-browser",
        capabilities: [
            "Connect existing domains",
            "Free SSL certificate",
            "Domain verification",
            "HTTPS by default"
        ],
        howItWorks: [
            { title: "Add Your Domain", description: "Enter your domain name in the dashboard" },
            { title: "Verify Ownership", description: "Update your DNS settings to verify ownership" },
            { title: "Go Live", description: "Your site is now accessible on your custom domain with SSL" }
        ],
        useCases: [
            { title: "Professional Presence", description: "Use yourbusiness.com instead of a subdomain" },
            { title: "Brand Consistency", description: "Match your domain to your business name" },
            { title: "SEO Benefits", description: "Custom domains are better for search engine rankings" }
        ],
        faqs: [
            { question: "Do I need to buy a domain separately?", answer: "Yes, you need to purchase your domain from a registrar, then connect it to TurboSteed." },
            { question: "Is SSL included?", answer: "Yes, free SSL certificates are automatically provisioned for all custom domains." },
            { question: "How long does setup take?", answer: "Once DNS is configured, your domain typically goes live within a few hours." }
        ]
    },
    "zip-upload": {
        name: "Zip Upload",
        slug: "zip-upload",
        tagline: "Upload Your Own Code",
        description: "Build with any tools you prefer. Upload your HTML, CSS, JS, or React static builds as a zip file. No vendor lock-in.",
        icon: "icon-upload",
        capabilities: [
            "Support for HTML/CSS/JS",
            "React static builds",
            "Any static framework",
            "No vendor lock-in"
        ],
        howItWorks: [
            { title: "Build Your Site", description: "Create your website using any tools you prefer" },
            { title: "Zip Your Files", description: "Compress your site files into a zip archive" },
            { title: "Upload and Deploy", description: "Upload the zip file and your site is live in seconds" }
        ],
        useCases: [
            { title: "Developers", description: "Use your preferred frameworks and tools" },
            { title: "Agencies", description: "Deploy custom-built client sites quickly" },
            { title: "Migration", description: "Move existing static sites to TurboSteed easily" }
        ],
        faqs: [
            { question: "What frameworks are supported?", answer: "Any framework that outputs static files: React, Vue, Gatsby, Next.js (static export), Hugo, Jekyll, and more." },
            { question: "What's the maximum file size?", answer: "File size limits depend on your plan, ranging from 3MB (Free) to 25MB (Startup/Agency)." },
            { question: "Can I use the CLI instead?", answer: "Yes — turbosteed deploy is the recommended way for developers." }
        ]
    },
    "api-access": {
        name: "API Access",
        slug: "api-access",
        tagline: "Full Programmatic Access",
        description: "REST API for site management, CMS, content updates, and asset management. Integrate with your existing tools or AI agents.",
        icon: "fa fa-code",
        secondaryCta: { text: "View API Docs", url: "/docs/rest-api.html" },
        capabilities: [
            "Site configuration API",
            "CMS schemas and entries",
            "Asset uploads",
            "Form data retrieval"
        ],
        howItWorks: [
            { title: "Get Your API Key", description: "Generate an API key from your dashboard" },
            { title: "Integrate", description: "Use our REST API endpoints in your workflows" },
            { title: "Automate", description: "Programmatically manage sites, content, and assets" }
        ],
        useCases: [
            { title: "AI Agents", description: "Connect AI coding agents via MCP or direct API calls" },
            { title: "Developers", description: "Build custom integrations and tools" },
            { title: "CI/CD Pipelines", description: "Deploy sites automatically from your build process" }
        ],
        faqs: [
            { question: "Is API access included in all plans?", answer: "API access is available on all plans. Some rate limits may apply." }
        ]
    },
    "asset-library": {
        name: "Asset Library",
        slug: "asset-library",
        tagline: "Manage Your Images and Files",
        description: "Upload, organize, and use images across your website. Upload via dashboard, API, or AI agent. Gallery view with easy URL copying.",
        icon: "icon-picture",
        capabilities: [
            "Image and file uploads",
            "Gallery view organization",
            "Quick URL copying",
            "API and MCP access"
        ],
        howItWorks: [
            { title: "Upload Files", description: "Upload images, PDFs, and other files via dashboard, API, or MCP" },
            { title: "Organize", description: "View and manage your assets in a gallery view" },
            { title: "Use Anywhere", description: "Copy URLs to use images in your site or share externally" }
        ],
        useCases: [
            { title: "AI-Generated Sites", description: "Let AI agents upload and manage assets programmatically" },
            { title: "Image-Heavy Sites", description: "Manage portfolios, galleries, and product images" },
            { title: "Document Sharing", description: "Host PDFs like menus, brochures, or price lists" }
        ],
        faqs: [
            { question: "What file types can I upload?", answer: "Images (JPG, PNG, GIF, WebP), PDFs, and common document formats." },
            { question: "Is there a storage limit?", answer: "Storage limits depend on your plan, ranging from 25MB (Free) to 150MB per site (Agency)." },
            { question: "Can AI agents upload assets?", answer: "Yes — use the API or MCP to upload assets programmatically." }
        ]
    },
    "webhooks": {
        name: "Webhooks",
        slug: "webhooks",
        tagline: "Automate Your Workflows",
        description: "Trigger workflows on form submissions. Connect to Zapier, n8n, or custom endpoints. Build automated notification and CRM integrations.",
        icon: "icon-refresh",
        heroImage: "/features/automation.png",
        capabilities: [
            "Webhooks for instant notifications on form submissions",
            "Zapier integration — connect to 7,000+ apps",
            "n8n compatible for open-source automation",
            "Custom endpoints — send data to your own servers",
            "API access to retrieve forms programmatically"
        ],
        howItWorks: [
            { title: "Configure Webhooks", description: "Add webhook URLs in your dashboard to receive notifications" },
            { title: "Connect Tools", description: "Paste your Zapier or n8n webhook URL" },
            { title: "Build Workflows", description: "Process submissions, notifications, or CRM updates automatically" }
        ],
        useCases: [
            { title: "Lead Routing", description: "Route leads directly to your CRM when forms are submitted" },
            { title: "Notifications", description: "Send instant alerts via Slack, email, or SMS" },
            { title: "Marketing", description: "Add contacts to email lists and trigger campaigns" }
        ],
        faqs: [
            { question: "What events can trigger webhooks?", answer: "Currently, contact form submissions. More events coming soon." },
            { question: "Can I connect to Zapier?", answer: "Yes — paste your Zapier webhook URL and you're connected." },
            { question: "Can I use custom endpoints?", answer: "Yes — send data to your own servers or serverless functions." }
        ]
    },
    "cicd": {
        name: "CI/CD",
        slug: "cicd",
        tagline: "Deploy from Terminal",
        description: "Install @turbosteed/cli via npm. Deploy sites with one command, manage CMS, assets, and forms. Integrate with CI/CD pipelines.",
        icon: "icon-tools",
        capabilities: [
            "CLI deployment — turbosteed deploy ships your site in seconds",
            "MCP server mode for AI agent integration",
            "Asset management — upload, list, delete",
            "CMS from CLI — schema and entry commands",
            "Contact forms — list and get commands",
            "CI/CD pipeline integration"
        ],
        howItWorks: [
            { title: "Install the CLI", description: "Run npm install -g @turbosteed/cli" },
            { title: "Configure Credentials", description: "Set up your Account SID and API Key" },
            { title: "Deploy", description: "Run turbosteed deploy from your project directory" },
            { title: "Or Use with AI", description: "Run with --mcp flag for Claude Code integration" }
        ],
        useCases: [
            { title: "Developers", description: "Deploy from terminal without touching the dashboard" },
            { title: "AI Integration", description: "Use as MCP server for Claude Code" },
            { title: "DevOps Teams", description: "Automate deployments in CI/CD pipelines" }
        ],
        faqs: [
            { question: "What CI/CD tools does this work with?", answer: "Any — GitHub Actions, GitLab CI, Jenkins, CircleCI, and more." },
            { question: "Can I use this with Claude Code?", answer: "Yes — run with --mcp flag to enable MCP server mode." },
            { question: "How do I install the CLI?", answer: "Run npm install -g @turbosteed/cli and configure your API credentials." }
        ]
    }
};
